// src/pages/QuizDashboard.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useQuizContext } from '../hooks/useQuizContext';
import QuizWrapper from '../components/QuizWrapper';
import ResultContentWrapper from '../components/ResultContentWrapper';
import PreQuizForm from '../components/PreQuizForm';

const API_ROOT = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Phase configuration - will be personalized with user's name
const getPhaseConfig = (firstName = 'there') => ({
  1: {
    title: `${firstName}, let's get to know you!`,
    subtitle: 'Help us understand what drives you'
  },
  2: {
    title: `Great start, ${firstName}! Let's find your purpose`,
    subtitle: 'Discover what excites you most'
  },
  3: {
    title: `Almost there, ${firstName}! Discovering your interests`,
    subtitle: 'Refine your academic path'
  }
});

/**
 * QuizDashboard
 * Main quiz orchestrator - handles 3-phase quiz flow
 * Uses mwc-layout.css for phase styling
 * 
 * Props:
 *  - universityName: string from tenant branding (displayed as "Why {name}?")
 */
export default function QuizDashboard({ universityName = '' }) {
  const { state, dispatch } = useQuizContext();
  const { 
    tenantId, 
    questions, 
    responses, 
    sessionId,
    resultData
  } = state;

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [loadingPhase, setLoadingPhase] = useState(null);
  const [currentPhase, setCurrentPhase] = useState(1);
  const [showPhaseTransition, setShowPhaseTransition] = useState(false);
  const [showPreQuizForm, setShowPreQuizForm] = useState(true);
  const [leadInfo, setLeadInfo] = useState(null);
  const [showPhaseCompleteButton, setShowPhaseCompleteButton] = useState(false);
  const isLoadingRef = useRef(false);

  // Get current phase questions only
  const currentPhaseQuestions = questions.filter(q => q.phase === currentPhase);
  const currentPhaseResponses = responses.filter(r => {
    const q = questions.find(qq => qq._id === r.questionId);
    return q && q.phase === currentPhase;
  });

  // Calculate progress within current phase
  const phaseProgress = currentPhaseQuestions.length > 0
    ? Math.round((currentPhaseResponses.length / currentPhaseQuestions.length) * 100)
    : 0;

  const handlePreQuizComplete = useCallback((newSessionId, leadData) => {
    setLeadInfo(leadData);
    dispatch({ type: 'SET_SESSION_ID', payload: newSessionId });
    setShowPreQuizForm(false);
  }, [dispatch]);

  const loadPhase1 = useCallback(async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    
    setLoadingPhase('initial');
    setSubmitError(null);

    try {
      const url = `${API_ROOT}/api/questions?tenantId=${encodeURIComponent(tenantId)}`;
      console.log('[QuizDashboard] Loading Phase 1:', url);
      
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`Failed to load questions: ${res.statusText}`);
      }

      const allQuestions = await res.json();
      const phase1 = allQuestions.filter(q => q.phase === 1);
      
      console.log('[QuizDashboard] Phase 1 loaded:', phase1.length);

      dispatch({ type: 'SET_QUESTIONS', payload: phase1 });
      dispatch({ type: 'SET_CURRENT_INDEX', payload: 0 });
      setCurrentPhase(1);
    } catch (err) {
      console.error('[QuizDashboard] Error loading Phase 1:', err);
      setSubmitError(err.message);
    } finally {
      setLoadingPhase(null);
      isLoadingRef.current = false;
    }
  }, [tenantId, dispatch]);

  const loadPhase2 = useCallback(async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    
    setLoadingPhase('phase2');

    try {
      const url = `${API_ROOT}/api/questions?tenantId=${encodeURIComponent(tenantId)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to load Phase 2: ${res.statusText}`);

      const allQuestions = await res.json();
      const phase2 = allQuestions.filter(q => q.phase === 2);
      
      console.log('[QuizDashboard] Phase 2 loaded:', phase2.length);

      const phase1 = questions.filter(q => q.phase === 1);
      dispatch({ type: 'SET_QUESTIONS', payload: [...phase1, ...phase2] });
      
      const firstPhase2Index = [...phase1, ...phase2].findIndex(q => q.phase === 2);
      dispatch({ type: 'SET_CURRENT_INDEX', payload: firstPhase2Index });
      
      setCurrentPhase(2);
      setShowPhaseTransition(false);
    } catch (err) {
      console.error('[QuizDashboard] Error loading Phase 2:', err);
      setSubmitError(err.message);
    } finally {
      setLoadingPhase(null);
      isLoadingRef.current = false;
    }
  }, [tenantId, questions, dispatch]);

  const loadPhase3 = useCallback(async (sid) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    
    setLoadingPhase('phase3');

    try {
      const url = `${API_ROOT}/api/questions?tenantId=${encodeURIComponent(tenantId)}&sessionId=${encodeURIComponent(sid)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to load Phase 3: ${res.statusText}`);

      const allQuestions = await res.json();
      const phase3 = allQuestions.filter(q => q.phase === 3);
      
      console.log('[QuizDashboard] Phase 3 loaded:', phase3.length);

      const phase1and2 = questions.filter(q => q.phase === 1 || q.phase === 2);
      dispatch({ type: 'SET_QUESTIONS', payload: [...phase1and2, ...phase3] });
      
      const firstPhase3Index = [...phase1and2, ...phase3].findIndex(q => q.phase === 3);
      dispatch({ type: 'SET_CURRENT_INDEX', payload: firstPhase3Index });
      
      setCurrentPhase(3);
      setShowPhaseTransition(false);
    } catch (err) {
      console.error('[QuizDashboard] Error loading Phase 3:', err);
      setSubmitError(err.message);
    } finally {
      setLoadingPhase(null);
      isLoadingRef.current = false;
    }
  }, [tenantId, questions, dispatch]);

  const handlePhase1Complete = useCallback(() => {
    console.log('[QuizDashboard] Phase 1 complete');
    setShowPhaseTransition(true);
    setTimeout(() => loadPhase2(), 2000);
  }, [loadPhase2]);

  const handlePhase2Complete = useCallback(async () => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;

    console.log('[QuizDashboard] Phase 2 complete, submitting with sessionId:', sessionId);
    setShowPhaseTransition(true);

    try {
      const phase1and2Responses = responses.filter(r => {
        const q = questions.find(qq => qq._id === r.questionId);
        return q && (q.phase === 1 || q.phase === 2);
      });

      const submitPayload = {
        tenantId,
        sessionId,
        responses: phase1and2Responses.map(r => ({
          questionId: r.questionId,
          selectedOptions: r.selectedOptions || [],
          selectedOptionIndexes: r.selectedOptionIndexes || []
        }))
      };

      console.log('[QuizDashboard] Submitting Phase 1+2 responses with sessionId:', sessionId);

      const submitRes = await fetch(`${API_ROOT}/api/responses/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitPayload)
      });

      if (!submitRes.ok) {
        throw new Error(`Failed to submit Phase 2: ${submitRes.statusText}`);
      }

      const submitData = await submitRes.json();
      const newSessionId = submitData.sessionId || submitData.session?.sessionId;
      
      if (!newSessionId) {
        throw new Error('No sessionId returned');
      }

      console.log('[QuizDashboard] Session created:', newSessionId);
      dispatch({ type: 'SET_SESSION_ID', payload: newSessionId });

      setTimeout(() => loadPhase3(newSessionId), 1000);
    } catch (err) {
      console.error('[QuizDashboard] Error completing Phase 2:', err);
      setSubmitError(err.message);
      setShowPhaseTransition(false);
    } finally {
      isLoadingRef.current = false;
    }
  }, [responses, questions, tenantId, sessionId, dispatch, loadPhase3]);

  const handleManualPhaseComplete = () => {
    setShowPhaseCompleteButton(false);
    if (currentPhase === 1) {
      handlePhase1Complete();
    } else if (currentPhase === 2) {
      handlePhase2Complete();
    }
  };

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      console.log('[QuizDashboard] Final submit');

      const submitPayload = {
        tenantId,
        sessionId,
        responses: responses.map(r => ({
          questionId: r.questionId,
          selectedOptions: r.selectedOptions || [],
          selectedOptionIndexes: r.selectedOptionIndexes || []
        })),
        final: true
      };

      const res = await fetch(`${API_ROOT}/api/responses/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitPayload)
      });

      if (!res.ok) {
        throw new Error(`Submit failed: ${res.statusText}`);
      }

      const data = await res.json();
      console.log('[QuizDashboard] Submit successful');

      dispatch({ type: 'SET_RESULT_DATA', payload: data });

      if (Array.isArray(data.session?.programs)) {
        dispatch({ type: 'SET_PROGRAMS', payload: data.session.programs });
      }

      if (Array.isArray(data.session?.resultContents)) {
        dispatch({ type: 'SET_RESULT_CONTENTS', payload: data.session.resultContents });
      }
    } catch (err) {
      console.error('[QuizDashboard] Submit error:', err);
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!showPreQuizForm && questions.length === 0 && !loadingPhase && !isLoadingRef.current) {
      loadPhase1();
    }
  }, [showPreQuizForm, questions.length, loadingPhase, loadPhase1]);

  useEffect(() => {
    if (currentPhaseQuestions.length === 0) return;
    if (showPhaseTransition || loadingPhase) return;

    const isPhase1or2 = currentPhase === 1 || currentPhase === 2;
    
    const currentPhaseQuestionIndexes = questions
      .map((q, idx) => q.phase === currentPhase ? idx : -1)
      .filter(idx => idx !== -1);
    const lastPhaseQuestionIndex = Math.max(...currentPhaseQuestionIndexes);
    const isOnLastQuestionOfPhase = state.currentQuestionIndex === lastPhaseQuestionIndex;

    setShowPhaseCompleteButton(isPhase1or2 && isOnLastQuestionOfPhase);
  }, [currentPhase, currentPhaseQuestions.length, state.currentQuestionIndex, questions, showPhaseTransition, loadingPhase]);

  // Show pre-quiz form
  if (showPreQuizForm) {
    return <PreQuizForm tenantId={tenantId} onComplete={handlePreQuizComplete} universityName={universityName} />;
  }

  // Show results
  if (resultData) {
    return (
      <div className="mwc-results">
        <ResultContentWrapper />

        <button 
          className="mwc-btn" 
          onClick={() => {
            dispatch({ type: 'RESET_QUIZ' });
            setCurrentPhase(1);
            setShowPhaseTransition(false);
            setShowPhaseCompleteButton(false);
            isLoadingRef.current = false;
            loadPhase1();
          }}
          style={{ marginTop: '32px' }}
        >
          Start New Quiz
        </button>
      </div>
    );
  }

  // Show phase transition
  if (showPhaseTransition) {
    const nextPhase = currentPhase + 1;
    const config = getPhaseConfig(leadInfo?.firstName)[nextPhase];
    
    return (
      <div className="mwc-phase-transition">
        <div style={{ maxWidth: 500, margin: '0 auto' }}>
          <div style={{ fontSize: 48, marginBottom: 20 }}>✨</div>
          <h2 className="mwc-phase-complete-title">
            Phase {currentPhase} Complete!
          </h2>
          <p style={{ fontSize: 18, color: '#64748B', marginBottom: 32 }}>
            Great work{leadInfo?.firstName ? `, ${leadInfo.firstName}` : ''}! Now let's move to...
          </p>
          <h3 className="mwc-next-phase-title">
            {config?.title}
          </h3>
          <p className="mwc-next-phase-subtitle">
            {config?.subtitle}
          </p>
          <div style={{ marginTop: 40 }}>
            <div className="mwc-spinner" />
          </div>
        </div>
      </div>
    );
  }

  // Show loading states
  if (loadingPhase === 'initial') {
    return (
      <div className="mwc-loading">
        <p>Loading your personalized quiz...</p>
      </div>
    );
  }

  if (loadingPhase === 'phase2' || loadingPhase === 'phase3') {
    return (
      <div className="mwc-loading">
        <p>Loading next phase...</p>
      </div>
    );
  }

  // Show quiz interface
  const config = getPhaseConfig(leadInfo?.firstName)[currentPhase];
  
  return (
    <div className="mwc-quiz-container">
      {/* Brand title inside the card */}
      {universityName && (
        <h1 className="mwc-brand-title">Why {universityName}?</h1>
      )}

      <div className="mwc-phase-header">
        <div className="mwc-phase-number">
          Phase {currentPhase} of 3
        </div>
        <h2 className="mwc-phase-title">
          {config.title}
        </h2>
        <p className="mwc-phase-subtitle">
          {config.subtitle}
        </p>
        
        <div className="mwc-phase-progress">
          <div className="mwc-phase-progress-text">
            <span>Progress</span>
            <span>{currentPhaseResponses.length} of {currentPhaseQuestions.length}</span>
          </div>
          <div className="mwc-phase-progress-bar">
            <div 
              className="mwc-phase-progress-fill"
              style={{ width: `${phaseProgress}%` }}
            />
          </div>
        </div>
      </div>

      <QuizWrapper 
        onSubmit={handleFinalSubmit}
        onPhaseComplete={handleManualPhaseComplete}
        submitting={submitting}
        submitError={submitError}
        hideSubmit={showPhaseCompleteButton}
        showPhaseComplete={showPhaseCompleteButton}
        phaseCompleteText="Next Phase"
      />
      
      {submitError && (
        <div className="mwc-error" role="alert">
          Error: {submitError}
        </div>
      )}
    </div>
  );
}